import { useEffect, useMemo, useRef, useState } from 'react'
import { FiX, FiCreditCard, FiFileText, FiLock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { getAuth } from 'firebase/auth'
import Button from './ui/Button'
import Card from './ui/Card'
import PixPaymentPanel from './PixPaymentPanel'
import { apiUrl, API_BASE } from '../config/api'
import LegalConsentText from './LegalConsentText'

const FIELD = 'input-field !min-h-[42px] !py-2.5 !px-4 !text-sm'

const DevicePurchaseModal = ({ onClose }) => {
  const [step, setStep] = useState(1)
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [pixCheckout, setPixCheckout] = useState(null)
  const [loading, setLoading] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const closeRef = useRef(null)

  const [addressData, setAddressData] = useState({
    name: '',
    phone: '',
    postalCode: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    city: '',
    state: '',
  })

  const [paymentData, setPaymentData] = useState({
    email: '',
    cpf: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  })

  const cepNumber = useMemo(() => addressData.postalCode.replace(/\D/g, ''), [addressData.postalCode])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    closeRef.current?.focus?.()
  }, [])

  const canGoToPayment = () => {
    const cpfNumber = paymentData.cpf.replace(/\D/g, '')
    const cep = addressData.postalCode.replace(/\D/g, '')

    if (!addressData.name || !addressData.phone) return 'Preencha nome e telefone.'
    if (!paymentData.email) return 'Informe seu e-mail.'
    if (cpfNumber.length < 11) return 'CPF inválido. Verifique os dígitos.'
    if (cep.length !== 8) return 'CEP inválido. Use 8 dígitos.'
    if (!addressData.address || !addressData.city || !addressData.state || !addressData.province) return 'Preencha todos os campos de endereço.'
    if (!addressData.addressNumber) return 'Informe o número do endereço.'
    return null
  }

  const validateCard = () => {
    const digits = paymentData.cardNumber.replace(/\D/g, '')
    if (digits.length < 13) return 'Número do cartão inválido.'
    if (!/^\d{2}\/\d{2}$/.test(paymentData.expiryDate)) return 'Validade inválida. Use MM/AA.'
    const cvvDigits = paymentData.cvv.replace(/\D/g, '')
    if (cvvDigits.length < 3) return 'CVV inválido.'
    if (!paymentData.cardName) return 'Informe o nome no cartão.'
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!acceptedLegal) {
      toast.error('Aceite a Política de Privacidade e os Termos de Uso para continuar.')
      return
    }
    setLoading(true)
    try {
      if (paymentMethod === 'card') {
        const cardErr = validateCard()
        if (cardErr) {
          toast.error(cardErr)
          return
        }
      }

      const auth = getAuth()
      const currentUser = auth.currentUser
      if (!currentUser) throw new Error('Faça login para comprar o dispositivo.')

      const token = await currentUser.getIdToken()

      const resp = await fetch(apiUrl('/api/asaas/buy-device'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentMethod,
          addressData: {
            ...addressData,
            email: paymentData.email,
            cpfCnpj: paymentData.cpf.replace(/\D/g, ''),
          },
          cardData:
            paymentMethod === 'card'
              ? {
                  cardNumber: paymentData.cardNumber,
                  cardName: paymentData.cardName,
                  expiryDate: paymentData.expiryDate,
                  cvv: paymentData.cvv,
                }
              : null,
        }),
      })

      const raw = await resp.text()
      const data = (() => {
        try { return JSON.parse(raw) } catch { return null }
      })()
      if (!data || !data.ok) {
        const prefix = raw?.slice?.(0, 120) || ''
        const hint = prefix.includes('<!DOCTYPE html') || prefix.includes('Cannot POST') ? `Teste: abra ${API_BASE}/api/health (tem que retornar JSON) e reinicie o backend se necessário.` : ''
        throw new Error(
          data?.error ||
            `Resposta inválida do backend (HTTP ${resp.status}). Início: ${prefix}${hint ? `\n${hint}` : ''}`
        )
      }

      const payment = data.payment
      if (data.pix) {
        setPixCheckout({ pix: data.pix, amount: payment?.value || 499.9 })
        setStep(3)
        toast.success('PIX gerado! Escaneie o QR Code ou copie o código.')
        return
      }

      if (payment?.bankSlipUrl) {
        window.open(payment.bankSlipUrl, '_blank')
      } else if (payment?.invoiceUrl) {
        window.open(payment.invoiceUrl, '_blank')
      }

      toast.success(
        paymentMethod === 'card'
          ? 'Pagamento do dispositivo criado com sucesso!'
          : 'Pedido do dispositivo criado! Enviaremos o boleto/links.'
      )
      onClose()
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Erro ao comprar dispositivo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />

      <div className="relative w-full max-w-xl min-w-0">
        <Card padding="lg" className="relative max-h-[90vh] overflow-y-auto">
          <button
            ref={closeRef}
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Fechar"
          >
            <FiX className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>

          <div className="mb-6 pt-2">
            <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-2">Dispositivo</p>
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-1">
              Comprar Sys[mv] - R01 por R$ 499,90
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Após o recebimento do pagamento, liberaremos sua assinatura.
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 1 ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                }`}
              >
                1
              </div>
              <span className={`text-sm font-semibold ${step === 1 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                Dados
              </span>
            </div>
            <span className="text-gray-400 text-xs">•••</span>
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 2 ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                }`}
              >
                2
              </div>
              <span className={`text-sm font-semibold ${step === 2 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                Pagamento
              </span>
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Nome</label>
                  <input
                    type="text"
                    required
                    value={addressData.name}
                    onChange={(e) => setAddressData({ ...addressData, name: e.target.value })}
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="form-label">Telefone</label>
                  <input
                    type="tel"
                    required
                    value={addressData.phone}
                    onChange={(e) => setAddressData({ ...addressData, phone: e.target.value })}
                    className={FIELD}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">E-mail</label>
                  <input
                    type="email"
                    required
                    value={paymentData.email}
                    onChange={(e) => setPaymentData({ ...paymentData, email: e.target.value })}
                    className={FIELD}
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <label className="form-label">CPF</label>
                  <input
                    type="text"
                    required
                    value={paymentData.cpf}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '')
                      value = value.replace(/(\d{3})(\d)/, '$1.$2')
                      value = value.replace(/(\d{3})(\d)/, '$1.$2')
                      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                      setPaymentData({ ...paymentData, cpf: value })
                    }}
                    className={FIELD}
                    placeholder="000.000.000-00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                  <label className="form-label">CEP</label>
                  <input
                    type="text"
                    required
                    value={addressData.postalCode}
                    onChange={(e) => setAddressData({ ...addressData, postalCode: e.target.value })}
                    className={FIELD}
                    placeholder="00000-000"
                    onBlur={async () => {
                      const cep = addressData.postalCode.replace(/\D/g, '')
                      if (cep.length !== 8) return
                      try {
                        setCepLoading(true)
                        const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
                        const data = await resp.json()
                        if (data.erro) {
                          toast.error('CEP não encontrado.')
                          return
                        }
                        setAddressData((prev) => ({
                          ...prev,
                          address: data.logradouro || prev.address,
                          province: data.bairro || prev.province,
                          city: data.localidade || prev.city,
                          state: data.uf || prev.state,
                        }))
                      } catch (err) {
                        console.error(err)
                        toast.error('Erro ao buscar CEP. Tente novamente.')
                      } finally {
                        setCepLoading(false)
                      }
                    }}
                  />
                  {cepLoading && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Buscando endereço...
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="form-label">Endereço</label>
                  <input
                    type="text"
                    required
                    value={addressData.address}
                    onChange={(e) => setAddressData({ ...addressData, address: e.target.value })}
                    className={FIELD}
                    placeholder="Rua, avenida..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Número</label>
                  <input
                    type="text"
                    required
                    value={addressData.addressNumber}
                    onChange={(e) => setAddressData({ ...addressData, addressNumber: e.target.value })}
                    className={FIELD}
                    placeholder="123"
                  />
                </div>
                <div>
                  <label className="form-label">Complemento</label>
                  <input
                    type="text"
                    value={addressData.complement}
                    onChange={(e) => setAddressData({ ...addressData, complement: e.target.value })}
                    className={FIELD}
                    placeholder="Apto, bloco..."
                  />
                </div>
                <div>
                  <label className="form-label">Bairro</label>
                  <input
                    type="text"
                    required
                    value={addressData.province}
                    onChange={(e) => setAddressData({ ...addressData, province: e.target.value })}
                    className={FIELD}
                    placeholder="Bairro"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Cidade</label>
                  <input
                    type="text"
                    required
                    value={addressData.city}
                    onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                    className={FIELD}
                    placeholder="Cidade"
                  />
                </div>
                <div>
                  <label className="form-label">UF</label>
                  <input
                    type="text"
                    required
                    maxLength={2}
                    value={addressData.state}
                    onChange={(e) => setAddressData({ ...addressData, state: e.target.value.toUpperCase() })}
                    className={FIELD}
                    placeholder="UF"
                  />
                </div>
              </div>

              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  disabled={loading || cepLoading}
                  onClick={() => {
                    const err = canGoToPayment()
                    if (err) {
                      toast.error(err)
                      return
                    }
                    setStep(2)
                  }}
                  className="w-full sm:w-auto"
                >
                  Continuar para pagamento
                </Button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`
                    flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold text-sm min-h-[52px]
                    focus:outline-none
                    ${paymentMethod === 'card' ? 'border-accent bg-accent text-white shadow-lg' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-accent'}
                  `}
                >
                  <FiCreditCard className="w-5 h-5" />
                  <span>Cartão</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('pix')}
                  className={`
                    flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold text-sm min-h-[52px]
                    focus:outline-none
                    ${paymentMethod === 'pix' ? 'border-accent bg-accent text-white shadow-lg' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-accent'}
                  `}
                >
                  <span className="font-extrabold">PIX</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('boleto')}
                  className={`
                    flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold text-sm min-h-[52px]
                    focus:outline-none
                    ${paymentMethod === 'boleto' ? 'border-accent bg-accent text-white shadow-lg' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-accent'}
                  `}
                >
                  <FiFileText className="w-5 h-5" />
                  <span>Boleto</span>
                </button>
              </div>

              {paymentMethod === 'card' ? (
                <>
                  <div>
                    <label className="form-label">Número do Cartão</label>
                    <input
                      type="text"
                      required
                      value={paymentData.cardNumber}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
                        setPaymentData({ ...paymentData, cardNumber: value })
                      }}
                      maxLength={19}
                      className={FIELD}
                      placeholder="0000 0000 0000 0000"
                    />
                  </div>

                  <div>
                    <label className="form-label">Nome no Cartão</label>
                    <input
                      type="text"
                      required
                      value={paymentData.cardName}
                      onChange={(e) => setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() })}
                      className={FIELD}
                      placeholder="NOME COMPLETO"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Validade</label>
                      <input
                        type="text"
                        required
                        value={paymentData.expiryDate}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '')
                          value = value.replace(/(\d{2})(?=\d)/g, '$1/')
                          setPaymentData({ ...paymentData, expiryDate: value.slice(0, 5) })
                        }}
                        maxLength={5}
                        className={FIELD}
                        placeholder="MM/AA"
                      />
                    </div>
                    <div>
                      <label className="form-label">CVV</label>
                      <input
                        type="text"
                        required
                        value={paymentData.cvv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                          setPaymentData({ ...paymentData, cvv: value })
                        }}
                        maxLength={4}
                        className={FIELD}
                        placeholder="000"
                      />
                    </div>
                  </div>
                </>
              ) : paymentMethod === 'pix' ? (
                <div className="bg-accent/10 dark:bg-accent/20 border-accent/30 border rounded-2xl p-4">
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    Ao confirmar, geraremos o QR Code PIX para pagamento imediato do dispositivo.
                  </p>
                </div>
              ) : (
                <div className="bg-accent/10 dark:bg-accent/20 border-accent/30 border rounded-2xl p-4">
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    O boleto será gerado e enviado para seu e-mail. O pagamento pode levar até 2 dias úteis para ser confirmado.
                  </p>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <input
                  type="checkbox"
                  checked={acceptedLegal}
                  onChange={(e) => setAcceptedLegal(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-accent"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  <LegalConsentText onLinkClick={onClose} /> Autorizo o tratamento dos meus dados pessoais e
                  de pagamento para a compra do dispositivo, incluindo compartilhamento com a central de pagamentos.
                </span>
              </label>

              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <FiLock className="w-5 h-5 text-accent flex-shrink-0" />
                <span>Pagamento seguro e criptografado.</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {loading ? 'Processando...' : paymentMethod === 'card' ? 'Pagar com Cartão' : paymentMethod === 'pix' ? 'Gerar PIX' : 'Gerar Boleto'}
                </Button>
              </div>
            </form>
          )}

          {step === 3 && pixCheckout && (
            <PixPaymentPanel
              pix={pixCheckout.pix}
              amountLabel={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pixCheckout.amount)}
              onDone={onClose}
            />
          )}
        </Card>
      </div>
    </div>
  )
}

export default DevicePurchaseModal

